import AddRegularDefault from 'mini-fluent-svg-icons/lib/custom-icons/AddRegularDefault';
import classnames from 'classnames/bind';
import getHeaderAriaLabel from '../utils/getHeaderAriaLabel';
import getSelectedFolderId from 'owa-mail-store/lib/utils/getSelectedFolderId';
import mailStore from 'owa-mail-store/lib/store/Store';
import type Module from 'mini-common-schema/lib/schema/Module';
import React, { useRef } from 'react';
import SearchRegular from 'mini-fluent-svg-icons/lib/icons/SearchRegular';
import setShouldShowSearchBox from 'mini-mail-list-store/lib/actions/setShouldShowSearchBox';
import strings from 'owa-strings/owa-mini-strings';
import styles from './MailSurfaceHeader.scss';
import switchModule from 'owa-mail-store/lib/actions/switchModule';
import { FloatingActionButton } from 'mini-floating-action-button';
import { getActivityIcon } from '../utils/getActivityIcon';
import { getFolderDisplayName } from 'owa-mail-store/lib/utils/getFolderDisplayName';
import { getFolderName } from 'mini-mail-list-store/lib/selectors/getFolderName';
import { getMailFilters } from '../utils/getMailFilters';
import { Header, HeaderTitle } from 'mini-header';
import { isMiniFeatureEnabled } from 'owa-mini-feature-flags';
import { isWhatsNewFeatureEnabled } from 'mini-activity-pane-store/lib/utils/isWhatsNewFeatureEnabled';
import { LAUNCH_MAIL_SEARCH } from 'mini-mail-workflow-tracking/lib/constants/SearchWorkflowConstants';
import { LAUNCH_SEARCH } from 'mini-search-common/lib/utils/searchWorkFlowConstants';
import { LeftHeaderContent } from 'mini-surface-header-common/lib/components/LeftHeaderContent';
import { observer } from 'mobx-react-lite';
import { onMailListFilterClickCallback } from 'mini-mail-list-filter';
import { onNewCompose } from '../utils/onNewCompose';
import { onOpenFolderPane } from '../utils/onOpenFolderPane';
import { PerformanceDatapoint } from 'owa-analytics';
import { RightHeaderContent } from 'mini-surface-header-common/lib/components/RightHeaderContent';
import { shouldShowMailListHeaderForFolderId } from 'mini-mail-list-view/lib/utils/mailList/shouldShowMailListHeaderForFolderId';
import { signalWorkflowEvent } from 'mini-workflow-manager';
import { type UnifiedTelemetryEvent } from 'telemetry/lib/schema/UnifiedTelemetryEvent';
import { UTActiveAppView } from '@outlook/unified_telemetry';
import type { HeaderIconType } from 'mini-header';

const cx = classnames.bind(styles);

interface MailSurfaceForwardedRefs {
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any --
     * See aka.ms/mini-lint-errors
     *	> Unexpected any. Specify a different type. */
    navigationButtonRef: React.RefObject<any>;
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any --
     * See aka.ms/mini-lint-errors
     *	> Unexpected any. Specify a different type. */
    searchButtonRef: React.RefObject<any>;
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any --
     * See aka.ms/mini-lint-errors
     *	> Unexpected any. Specify a different type. */
    composeButtonRef: React.RefObject<any>;
}

interface MailSurfaceHeaderProps {
    onSearchStartCallback?: (searchPerfDataPoint: PerformanceDatapoint) => void;
    forwardedRefs?: MailSurfaceForwardedRefs;
}

const MailSurfaceHeader = (props: MailSurfaceHeaderProps) => {
    const {
        onSearchStartCallback,
        forwardedRefs: {
            navigationButtonRef,
            searchButtonRef,
            composeButtonRef,
        } = {} as MailSurfaceForwardedRefs,
    } = props;

    // create the refs
    const mailFilterRef = useRef<HTMLDivElement>();

    const { folderTable } = mailStore.folders;
    const selectedFolderId = getSelectedFolderId();
    let folderName: string;

    if (selectedFolderId && folderTable.size > 0) {
        const selectedFolder = folderTable.get(selectedFolderId);

        // Fix for Bug: 34288
        // selectedFolder comes as null some times when pressing hardware back button multiple times.
        // Right now, doing a null check to avoid the error but created a WI 34429 on this.
        folderName = getFolderDisplayName(selectedFolder);
    }

    // The right icons
    const onSearchStart = () => {
        onSearchStartCallback &&
            onSearchStartCallback(new PerformanceDatapoint('SearchBoxAppearance'));

        if (isMiniFeatureEnabled('mini-unified-search')) {
            signalWorkflowEvent(
                LAUNCH_SEARCH,
                { source: getFolderName() },
                {
                    isCore: true,
                    unifiedTelemetry: {
                        eventName: 'PerformSearch',
                        data: {
                            ActiveAppView: UTActiveAppView.Mail,
                        },
                    },
                }
            );
            switchModule(6);
        } else {
            signalWorkflowEvent(LAUNCH_MAIL_SEARCH, { source: getFolderName() });
            setShouldShowSearchBox(true /* shouldShowSearchBox */);
        }
    };

    const mailFilters = getMailFilters(selectedFolderId);
    const mailFilterButtons = mailFilters.map(filter => ({
        icon: filter.icon,
        onClick: onMailListFilterClickCallback(filter.activeViewFilter, mailFilterRef),
        iconRef: mailFilterRef,
        ariaLabel: filter.ariaLabel,
        iconStyle: classnames(styles.icon, filter.isPressed && styles.isButtonPressed),
    }));

    const searchIcon = {
        icon: SearchRegular,
        onClick: onSearchStart,
        iconRef: searchButtonRef,
        ariaLabel: strings.searchButtonAriaLabel,
        id: 'search-icon',
        iconStyle: styles.icon,
    };
    const rightIcons: HeaderIconType[] = [
        ...mailFilterButtons,
        (isWhatsNewFeatureEnabled() || isMiniFeatureEnabled('mini-reactions')) && getActivityIcon(),
        searchIcon,
    ].filter(Boolean);

    return (
        <>
            <Header
                leftContent={
                    <LeftHeaderContent
                        navigationButtonRef={navigationButtonRef}
                        onOpenFolderPane={onOpenFolderPane}
                        ariaLabel={strings.folderNavigationButtonAriaLabel}
                    />
                }
                rightContent={
                    <RightHeaderContent
                        rightIcons={rightIcons}
                        navigationButtonRef={navigationButtonRef}
                        ariaLabel={strings.folderNavigationButtonAriaLabel}
                    />
                }
                titleContent={
                    <HeaderTitle
                        title={folderName} // empty string to make sure HeaderTitle is rendered
                        titleStyles={styles.primaryTitle}
                        titleAriaLabel={getHeaderAriaLabel(folderName)}
                    />
                }
                className={cx(
                    styles.mailSurfaceHeader,
                    shouldShowMailListHeaderForFolderId(selectedFolderId) &&
                        styles.hasSecondaryHeader
                )}
                titleAlign={'left'}
            />

            {/* Should be placed immediately after header for correct accessibility navigation order  */}
            <FloatingActionButton
                ref={composeButtonRef}
                icon={AddRegularDefault}
                onClick={onNewCompose}
                ariaHasPopup={true}
                ariaLabel={strings.composeNewMailAriaLabel}
            />
        </>
    );
};

export default observer(MailSurfaceHeader);
